// CreateButton — the primary "create" action for a ListHeader. A thin intent
// wrapper over SplitButton (COMPONENTS §Catalog): primary fill + a `+` icon,
// labelled on sm+, a square `+` on mobile. Two shapes from one component:
//   • simple  → one create target, fires `onClick` directly
//   • split   → multiple targets (e.g. New / Associate existing) open a dropdown
//     (the square opens the same menu on mobile)
// `disabled` (preconditions unmet) and `loading` (create in-flight) are
// independent, inherited from SplitButton → <Button>.

import { icons } from '../ui/icons.js';
import { SplitButton, type SplitButtonAction } from '../ui/SplitButton.js';

export interface CreateButtonProps {
  /** i18n'd label, e.g. "Create site" / "Add user". Also the icon button's accessible name. */
  label: string;
  /** Single-target create. Ignored when `actions` holds ≥2 targets. */
  onClick?: () => void;
  /** Split create — ≥2 targets render a dropdown (e.g. New user / Associate existing). */
  actions?: SplitButtonAction[];
  /** Accessible name for the split dropdown. Defaults to `label`. */
  menuLabel?: string;
  /** Preconditions unmet — blocks the action (independent of loading). */
  disabled?: boolean;
  /** Create is in-flight — shows a spinner and blocks the action. */
  loading?: boolean;
}

export function CreateButton({ label, onClick, actions, menuLabel, disabled = false, loading = false }: CreateButtonProps) {
  return (
    <SplitButton
      variant="primary"
      icon={<icons.add size={16} aria-hidden />}
      label={label}
      onPress={onClick}
      actions={actions}
      menuLabel={menuLabel}
      disabled={disabled}
      loading={loading}
    />
  );
}
